import { Location, ProductDraft, Unit } from "@/types/general";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Collapse } from "@mui/material";
import { isErrorWithMessage } from "@redux/api/query";
import { useGetModuleLocationsQuery } from "@redux/features/locations/locationsApi";
import { useUpdateProductDraftInformationMutation } from "@redux/features/productDrafts/productDraftsApi";
import { useGetModuleSuppliersQuery } from "@redux/features/suppliers/suppliersApi";
import { useGetModuleTaxesQuery } from "@redux/features/taxes/taxesApi";
import { useGetModuleUnitsQuery } from "@redux/features/units/unitsApi";
import { useAppSelector } from "@redux/hooks";
import BaseButton from "@reusables/BaseButton";
import BaseCheckbox from "@reusables/BaseCheckbox";
import BaseCustomFieldsSection from "@reusables/BaseCustomFieldsSection";
import BaseImageUploader from "@reusables/BaseImageUploader";
import BaseInput from "@reusables/BaseInput";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import BaseTooltip from "@reusables/BaseTooltip";
import { BaseLoadingBlocker } from "@reusables/blockers/BaseLoadingBlocker";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseModal from "@reusables/Modals/BaseModal";
import CategoryCommandDropdown from "@ready/CategoryCommandDropdown";
import { ReactComponent as QuestionSVG } from "@assets/icons/ic_question.svg";
import { XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
    assertValidDefaultLocation,
    productDraftFormSchema,
    ProductDraftFormValues,
} from "./types";

type CreationEditingLayoutProps = {
    productDraft: ProductDraft.Extended;
};

type FormLocation = {
    store: { id: number; name: string };
    section: { id: number; name: string } | null;
};

type ProductDraftNonImageUpdate = Omit<ProductDraft.DTO.UpdateInformation, "image">;

const imageDomainURLPrefix = import.meta.env.VITE_API_URL;

function normalizeLocation(location: Location.Merged): FormLocation {
    return {
        store: location.store,
        section: location.section ?? null,
    };
}

function getExistingImageUrl(productDraft: ProductDraft.Extended): string | undefined {
    const path = productDraft.product.image_path ?? productDraft.product.image;
    if (!path) return undefined;
    if (/^https?:\/\//.test(path)) return path;
    return `${imageDomainURLPrefix}${path}`;
}

function getDefaultValues(productDraft: ProductDraft.Extended): ProductDraftFormValues {
    assertValidDefaultLocation(productDraft);
    const product = productDraft.product;

    return {
        general: {
            name: product.name,
            code: product.code ?? "",
            supplier_product_code: product.supplier_product_code ?? null,
            barcode: product.barcode ?? null,
            category: product.category ?? null,
            unit: product.unit ?? null,
            default_location: product.default_location
                ? normalizeLocation(product.default_location)
                : null,
            supplier: product.supplier ?? null,
            tax: product.tax ?? null,
            unit_equivalents:
                product.unit_equivalents?.map((item) => ({
                    unit_id: item.unit.id,
                    quantity: Number(item.quantity),
                })) ?? [],
        },
        prices: {
            purchase_price: product.prices.purchase_price ?? null,
            selling_price: product.prices.selling_price ?? null,
            extra_cost: product.prices.extra_cost ?? null,
        },
        inventory: {
            min_inventory_quantity: product.min_inventory_quantity ?? null,
            min_purchase_quantity: product.min_purchase_quantity ?? null,
            min_sale_quantity: product.min_sale_quantity ?? null,
        },
        weights_and_sizes: {
            weight: product.weights_and_sizes.weight ?? null,
            CBM: product.weights_and_sizes.CBM ?? null,
            width: product.weights_and_sizes.width ?? null,
            height: product.weights_and_sizes.height ?? null,
            length: product.weights_and_sizes.length ?? null,
        },
        options: {
            is_RFID: product.is_RFID ?? false,
            is_batch_number: product.is_batch_number ?? false,
            is_serial_number: product.is_serial_number ?? false,
            is_component: product.is_component ?? false,
        },
        description: {
            image: getExistingImageUrl(productDraft),
            description: product.description ?? null,
        },
        is_service: product.is_service ?? false,
        custom_fields: productDraft.custom_fields ?? [],
    };
}

export default function CreationEditingLayout({ productDraft }: CreationEditingLayoutProps) {
    const { t } = useTranslation("", { keyPrefix: "productDrafts" });
    const history = useHistory();
    const currency = useAppSelector((state) => state.auth.user?.company?.currency);
    const form = useForm<ProductDraftFormValues>({
        resolver: zodResolver(productDraftFormSchema),
        defaultValues: getDefaultValues(productDraft),
    });
    const {
        control,
        formState: { dirtyFields, isDirty },
    } = form;

    const [updateProductDraft, updateResult] = useUpdateProductDraftInformationMutation();
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [isAddingEquivalent, setIsAddingEquivalent] = useState(false);

    const isService = useWatch({ control, name: "is_service" });
    const baseUnit = useWatch({ control, name: "general.unit" });
    const unitEquivalents = useWatch({ control, name: "general.unit_equivalents" });

    const { data: units = [], isLoading: unitsLoading } = useGetModuleUnitsQuery("product");
    const { data: rawLocations = [], isLoading: locationsLoading } =
        useGetModuleLocationsQuery("product");
    const { data: suppliers = [], isLoading: suppliersLoading } =
        useGetModuleSuppliersQuery("product");
    const { data: taxes = [], isLoading: taxesLoading } = useGetModuleTaxesQuery("product");

    const locations = useMemo(() => rawLocations.map(normalizeLocation), [rawLocations]);

    const {
        fields: equivalentFields,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "general.unit_equivalents",
    });
    const isBaseUnitSelected = Boolean(baseUnit?.id);
    const showUnitEquivalents = isBaseUnitSelected || equivalentFields.length > 0;

    const equivalentOptions = (rowIndex: number): Unit.Root[] => {
        const selectedIds = unitEquivalents
            .filter((_, index) => index !== rowIndex)
            .map((item) => item.unit_id);
        return units.filter((unit) => unit.id !== baseUnit?.id && !selectedIds.includes(unit.id));
    };

    const mapDirtyFields = (values: ProductDraftFormValues): ProductDraftNonImageUpdate => {
        const update: ProductDraftNonImageUpdate = {};
        const generalDirty = dirtyFields.general;

        if (generalDirty?.name) update.name = values.general.name;
        if (generalDirty?.code) update.code = values.general.code;
        if (generalDirty?.supplier_product_code) {
            update.supplier_product_code = values.general.supplier_product_code;
        }
        if (generalDirty?.barcode) update.barcode = values.general.barcode;
        if (generalDirty?.category) update.category = values.general.category?.id ?? null;
        if (generalDirty?.unit) update.unit = values.general.unit?.id ?? null;
        if (generalDirty?.supplier) update.supplier = values.general.supplier?.id ?? null;
        if (generalDirty?.tax) update.tax = values.general.tax?.id ?? null;
        if (generalDirty?.default_location && values.general.default_location) {
            update.default_location = {
                store: values.general.default_location.store.id,
                section: values.general.default_location.section?.id ?? null,
            };
        }
        if (generalDirty?.unit_equivalents) {
            update.unit_equivalents = values.general.unit_equivalents.map((item) => ({
                unit_id: item.unit_id,
                quantity: item.quantity,
            }));
        }

        if (dirtyFields.prices?.purchase_price) {
            update.prices = {
                ...update.prices,
                purchase_price: values.prices.purchase_price,
            };
        }
        if (dirtyFields.prices?.selling_price) {
            update.prices = {
                ...update.prices,
                selling_price: values.prices.selling_price,
            };
        }
        if (dirtyFields.prices?.extra_cost) {
            update.prices = {
                ...update.prices,
                extra_cost: values.prices.extra_cost,
            };
        }

        if (dirtyFields.inventory?.min_inventory_quantity) {
            update.min_inventory_quantity = values.inventory.min_inventory_quantity;
        }
        if (dirtyFields.inventory?.min_purchase_quantity) {
            update.min_purchase_quantity = values.inventory.min_purchase_quantity;
        }
        if (dirtyFields.inventory?.min_sale_quantity) {
            update.min_sale_quantity = values.inventory.min_sale_quantity;
        }

        const weightsDirty = dirtyFields.weights_and_sizes;
        if (weightsDirty?.weight) {
            update.weights_and_sizes = {
                ...update.weights_and_sizes,
                weight: values.weights_and_sizes.weight,
            };
        }
        if (weightsDirty?.CBM) {
            update.weights_and_sizes = {
                ...update.weights_and_sizes,
                CBM: values.weights_and_sizes.CBM,
            };
        }
        if (weightsDirty?.width) {
            update.weights_and_sizes = {
                ...update.weights_and_sizes,
                width: values.weights_and_sizes.width,
            };
        }
        if (weightsDirty?.height) {
            update.weights_and_sizes = {
                ...update.weights_and_sizes,
                height: values.weights_and_sizes.height,
            };
        }
        if (weightsDirty?.length) {
            update.weights_and_sizes = {
                ...update.weights_and_sizes,
                length: values.weights_and_sizes.length,
            };
        }

        if (dirtyFields.options?.is_RFID) update.is_RFID = values.options.is_RFID;
        if (dirtyFields.options?.is_batch_number) {
            update.is_batch_number = values.options.is_batch_number;
        }
        if (dirtyFields.options?.is_serial_number) {
            update.is_serial_number = values.options.is_serial_number;
        }
        if (dirtyFields.options?.is_component) {
            update.is_component = values.options.is_component;
        }

        if (dirtyFields.description?.description) {
            update.description = values.description.description;
        }
        if (dirtyFields.custom_fields) {
            update.custom_fields = values.custom_fields
                .filter((field) => Boolean(field.value))
                .map((field) => ({ id: field.id, value: field.value as string }));
        }

        return update;
    };

    const onSubmit = form.handleSubmit((values) => {
        if (!isDirty) return;

        const update = mapDirtyFields(values);
        const currentImage = values.description.image;
        const imageDirty = Boolean(dirtyFields.description?.image);
        const updatePromise =
            imageDirty && currentImage instanceof File
                ? updateProductDraft({
                      draft_id: productDraft.id,
                      ...update,
                      image: currentImage,
                  }).unwrap()
                : imageDirty && !currentImage
                ? updateProductDraft({
                      draft_id: productDraft.id,
                      ...update,
                      image: null,
                  }).unwrap()
                : updateProductDraft({
                      draft_id: productDraft.id,
                      ...update,
                  }).unwrap();

        updatePromise
            .then(() => {
                toast.success(t("editor.responses.success"));
                history.push("/dashboard/product-drafts");
            })
            .catch((error: unknown) => {
                if (isErrorWithMessage(error)) {
                    toast.error(error.message);
                    return;
                }
                throw error;
            });
    });

    const cancel = () => {
        if (isDirty) {
            setDiscardModalOpen(true);
            return;
        }
        history.push("/dashboard/product-drafts");
    };

    const currencyIcon = currency?.symbol
        ? {
              right: {
                  el: <span className="text-purple-500">{currency.symbol}</span>,
                  offset: "10px",
              },
          }
        : undefined;

    return (
        <BaseLoadingBlocker active={updateResult.isLoading}>
            <FormProvider {...form}>
                <form className="space-y-[40px]" onSubmit={onSubmit}>
                    <BaseInputsGrid
                        title={t("editor.sections.general")}
                        cols={{ xs: 1, md: 2, xl: 3 }}
                    >
                        <Controller
                            name="general.name"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("general.fields.productName")}
                                    required
                                />
                            )}
                        />
                        <Controller
                            name="general.code"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("general.fields.productCode")}
                                />
                            )}
                        />
                        <Controller
                            name="general.supplier_product_code"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("general.fields.supplierProductCode")}
                                />
                            )}
                        />
                        {!isService && (
                            <Controller
                                name="general.barcode"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <BaseInput
                                        {...field}
                                        error={fieldState.error}
                                        label={t("general.fields.barcode")}
                                    />
                                )}
                            />
                        )}
                        <Controller
                            name="general.category"
                            control={control}
                            render={({ field, fieldState }) => (
                                <div>
                                    <p className="mb-2 text-inputs-label-dim">
                                        {t("general.fields.category.label")}
                                    </p>
                                    <CategoryCommandDropdown
                                        value={field.value?.id}
                                        onSelect={(event) =>
                                            field.onChange(event.current?.item ?? null)
                                        }
                                        multiple={false}
                                        module="product"
                                        error={fieldState.error}
                                    />
                                </div>
                            )}
                        />
                        <Controller
                            name="general.unit"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    value={field.value}
                                    onChange={(option) => field.onChange(option ?? null)}
                                    error={fieldState.error}
                                    label={t("general.fields.unit.label")}
                                    placeholder={t("general.fields.unit.placeholder")}
                                    options={units}
                                    isLoading={unitsLoading}
                                    autocomplete
                                    getter={{
                                        key: (option) => option.id,
                                        label: (option) => option.name,
                                        caption: (option) => option.code,
                                    }}
                                />
                            )}
                        />
                        {!isService && (
                            <Controller
                                name="general.default_location"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <BaseDropdown
                                        value={field.value}
                                        onChange={(option) => field.onChange(option ?? null)}
                                        error={fieldState.error}
                                        label={t("general.fields.defaultLocation.label")}
                                        placeholder={t(
                                            "general.fields.defaultLocation.placeholder"
                                        )}
                                        options={locations}
                                        isLoading={locationsLoading}
                                        autocomplete
                                        virtualize
                                        required
                                        getter={{
                                            key: (option) =>
                                                option.section
                                                    ? `${option.store.id}_${option.section.id}`
                                                    : option.store.id,
                                            label: (option) =>
                                                `${option.store.name}${
                                                    option.section
                                                        ? ` - ${option.section.name}`
                                                        : ""
                                                }`,
                                        }}
                                    />
                                )}
                            />
                        )}
                        <Controller
                            name="general.supplier"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    value={field.value}
                                    onChange={(option) => field.onChange(option ?? null)}
                                    error={fieldState.error}
                                    label={t("general.fields.primarySupplier.label")}
                                    placeholder={t("general.fields.primarySupplier.placeholder")}
                                    options={suppliers}
                                    isLoading={suppliersLoading}
                                    autocomplete
                                    getter={{
                                        key: (option) => option.id,
                                        label: (option) => option.name,
                                        caption: (option) => option.code,
                                    }}
                                />
                            )}
                        />
                        <Controller
                            name="general.tax"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    value={field.value}
                                    onChange={(option) => field.onChange(option ?? null)}
                                    error={fieldState.error}
                                    label={t("general.fields.defaultTax.label")}
                                    placeholder={t("general.fields.defaultTax.placeholder")}
                                    options={taxes}
                                    isLoading={taxesLoading}
                                    autocomplete
                                    getter={{
                                        key: (option) => option.id,
                                        label: (option) => option.name,
                                        caption: (option) => `${option.rate}%`,
                                    }}
                                />
                            )}
                        />
                    </BaseInputsGrid>

                    {showUnitEquivalents && (
                        <div className="space-y-[16px]">
                            <Collapse in={Boolean(form.formState.errors.general?.unit_equivalents)}>
                                <Alert severity="error">
                                    {t("editor.validation.unitEquivalents")}
                                </Alert>
                            </Collapse>

                            <div className="py-[12px] px-[16px] border border-[#D8D8D8] rounded-2xl">
                                <h5 className="text-[#686868] text-sm font-medium">
                                    {t("general.unitEquivalents.header")}
                                </h5>
                                <div className="flex gap-2">
                                    <div className="flex flex-col gap-[8px] w-full">
                                        {equivalentFields.map((unit, index) => (
                                            <div
                                                key={unit.id}
                                                className="flex items-center gap-[8px]"
                                            >
                                                <div className="flex gap-[8px]">
                                                    <div style={{ width: "100px" }}>
                                                        <Controller
                                                            name={`general.unit_equivalents.${index}.quantity`}
                                                            control={control}
                                                            render={({ field, fieldState }) => (
                                                                <BaseInput
                                                                    {...field}
                                                                    error={fieldState.error}
                                                                    type="number"
                                                                />
                                                            )}
                                                        />
                                                    </div>

                                                    <div style={{ width: "200px" }}>
                                                        <Controller
                                                            name={`general.unit_equivalents.${index}.unit_id`}
                                                            control={control}
                                                            render={({ field, fieldState }) => (
                                                                <BaseDropdown
                                                                    {...field}
                                                                    error={fieldState.error}
                                                                    options={equivalentOptions(
                                                                        index
                                                                    )}
                                                                    value={field.value}
                                                                    onChange={(item) =>
                                                                        field.onChange(
                                                                            item?.id ?? 0
                                                                        )
                                                                    }
                                                                    getter={{
                                                                        key: (option) => option.id,
                                                                        label: (option) =>
                                                                            option.name,
                                                                        caption: (option) =>
                                                                            option.code ??
                                                                            option.id,
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                </div>

                                                {isAddingEquivalent &&
                                                    index === equivalentFields.length - 1 && (
                                                        <>
                                                            <BaseButton
                                                                size="xs"
                                                                text={t(
                                                                    "general.unitEquivalents.save"
                                                                )}
                                                                color="transparent"
                                                                textColor="#7556FA"
                                                                style={{
                                                                    backgroundColor: "#D9DAFD",
                                                                }}
                                                                buttonWidth="75px"
                                                                className="font-semibold"
                                                                type="button"
                                                                onClick={() =>
                                                                    setIsAddingEquivalent(false)
                                                                }
                                                            />

                                                            <BaseButton
                                                                size="xs"
                                                                text={t(
                                                                    "general.unitEquivalents.cancel"
                                                                )}
                                                                color="transparent"
                                                                textColor="#686868"
                                                                buttonWidth="75px"
                                                                className="font-semibold"
                                                                type="button"
                                                                onClick={() => {
                                                                    remove(index);
                                                                    setIsAddingEquivalent(false);
                                                                }}
                                                            />
                                                        </>
                                                    )}

                                                {!isAddingEquivalent && (
                                                    <div className="w-fit">
                                                        <BaseButton
                                                            size="xs"
                                                            text={t(
                                                                "general.unitEquivalents.delete"
                                                            )}
                                                            color="#F4F4F4"
                                                            textColor="#686868"
                                                            type="button"
                                                            onClick={() => remove(index)}
                                                            buttonWidth="75px"
                                                            className="font-semibold"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {!isAddingEquivalent && (
                                            <div className="w-fit">
                                                <BaseButton
                                                    size="md"
                                                    text={t("general.unitEquivalents.button")}
                                                    color="#F4F4F4"
                                                    textColor="#686868"
                                                    iconPos="left"
                                                    type="button"
                                                    disabled={!isBaseUnitSelected}
                                                    onClick={() => {
                                                        append({ quantity: 0, unit_id: 0 });
                                                        setIsAddingEquivalent(true);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <BaseInputsGrid title={t("general.categories.prices")} cols={{ xs: 1, md: 3 }}>
                        {(
                            [
                                ["purchase_price", "purchasePrice"],
                                ["selling_price", "sellingPrice"],
                                ["extra_cost", "extraCost"],
                            ] as const
                        ).map(([name, label]) => (
                            <Controller
                                key={name}
                                name={`prices.${name}`}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <BaseInput
                                        {...field}
                                        error={fieldState.error}
                                        label={t(`general.fields.${label}`)}
                                        type="number"
                                        step="any"
                                        sx={{
                                            "& .MuiInputBase-input": {
                                                paddingRight: "25px",
                                            },
                                        }}
                                        icon={currencyIcon}
                                    />
                                )}
                            />
                        ))}
                    </BaseInputsGrid>

                    <BaseInputsGrid
                        title={t("general.categories.inventory")}
                        cols={{ xs: 1, md: 2, xl: 3 }}
                    >
                        {!isService && (
                            <Controller
                                name="inventory.min_inventory_quantity"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <BaseInput
                                        {...field}
                                        error={fieldState.error}
                                        label={t("general.fields.invQty")}
                                        type="number"
                                        step="any"
                                    />
                                )}
                            />
                        )}
                        <Controller
                            name="inventory.min_purchase_quantity"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("general.fields.purchaseQty")}
                                    type="number"
                                    step="any"
                                />
                            )}
                        />
                        <Controller
                            name="inventory.min_sale_quantity"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("general.fields.saleQty")}
                                    type="number"
                                    step="any"
                                />
                            )}
                        />
                    </BaseInputsGrid>

                    {!isService && (
                        <BaseInputsGrid
                            title={t("general.categories.options")}
                            cols={{ xs: 1, md: 2, xl: 4 }}
                        >
                            {(
                                [
                                    ["is_RFID", "rfid"],
                                    ["is_batch_number", "batchNumber"],
                                    ["is_serial_number", "serialNumber"],
                                    ["is_component", "components"],
                                ] as const
                            ).map(([name, label]) => (
                                <div key={name} className="flex items-center">
                                    <Controller
                                        name={`options.${name}`}
                                        control={control}
                                        render={({ field }) => (
                                            <BaseCheckbox
                                                value={field.value}
                                                onChange={field.onChange}
                                                label={
                                                    <span className="ml-[16px]">
                                                        {t(`general.fields.${label}.title`)}
                                                    </span>
                                                }
                                            />
                                        )}
                                    />
                                    <BaseTooltip
                                        title={`${t(`general.fields.${label}.tip`)}`}
                                        placement="top"
                                    >
                                        <QuestionSVG className="text-specialIcons-question" />
                                    </BaseTooltip>
                                </div>
                            ))}
                        </BaseInputsGrid>
                    )}

                    {!isService && (
                        <BaseInputsGrid
                            title={t("general.categories.weights")}
                            cols={{ xs: 1, md: 3, xl: 5 }}
                        >
                            {(
                                [
                                    ["weight", "weight", "kg"],
                                    ["CBM", "cbm", undefined],
                                    ["width", "width", "mm"],
                                    ["height", "height", "mm"],
                                    ["length", "length", "mm"],
                                ] as const
                            ).map(([name, label, unit]) => (
                                <Controller
                                    key={name}
                                    name={`weights_and_sizes.${name}`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <BaseInput
                                            {...field}
                                            error={fieldState.error}
                                            label={`${t(`general.fields.${label}`)}${
                                                unit ? ` (${unit})` : ""
                                            }`}
                                            type="number"
                                            step="any"
                                        />
                                    )}
                                />
                            ))}
                        </BaseInputsGrid>
                    )}

                    <BaseCustomFieldsSection.Mutation mode="editing" module="product" />

                    <div className="space-y-[24px]">
                        <div className="text-xl text-accent">
                            {t("general.categories.attachments")}
                        </div>
                        <div className="flex flex-col gap-[24px] md:flex-row md:items-start md:gap-[40px]">
                            <Controller
                                name="description.image"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <div className="relative w-[120px] shrink-0">
                                        {field.value && (
                                            <BaseMaterialIconButton
                                                icon={<XCircle size={20} />}
                                                size="small"
                                                style="dim"
                                                className="absolute right-[-12px] top-[-12px] z-10"
                                                aria-label={t("editor.image.remove")}
                                                tip={t("editor.image.remove")}
                                                onClick={() => field.onChange(undefined)}
                                            />
                                        )}
                                        <BaseImageUploader
                                            value={field.value as File | undefined}
                                            src={
                                                typeof field.value === "string"
                                                    ? field.value
                                                    : undefined
                                            }
                                            onChange={field.onChange}
                                            error={fieldState.error}
                                            size={{ width: 120, height: 120 }}
                                        />
                                    </div>
                                )}
                            />
                            <div className="grow">
                                <Controller
                                    name="description.description"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <BaseInput
                                            {...field}
                                            error={fieldState.error}
                                            label={t("general.fields.attachmentDescription")}
                                            multiline
                                            rows={4}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-center gap-[24px]">
                        <BaseButton
                            type="button"
                            size="md"
                            text={t("createEditProduct.buttons.cancel")}
                            primaryOutlined
                            buttonWidth="200px"
                            className="font-semibold"
                            onClick={cancel}
                        />
                        <BaseButton
                            type="submit"
                            size="md"
                            text={t("createEditProduct.buttons.save")}
                            buttonWidth="200px"
                            className="font-semibold"
                            disabled={!isDirty || updateResult.isLoading}
                        />
                    </div>
                </form>
            </FormProvider>

            <BaseModal
                isOpen={discardModalOpen}
                onClose={() => setDiscardModalOpen(false)}
                width={700}
                padding="56px"
                position="center"
            >
                <div className="space-y-[32px]">
                    <div className="modal-title text-center">{t("editor.discard.heading")}</div>
                    <div className="text-center text-accent">{t("editor.discard.body")}</div>
                    <div className="grid grid-cols-2 gap-[16px]">
                        <BaseButton
                            type="button"
                            size="md"
                            text={t("editor.discard.keepEditing")}
                            primaryOutlined
                            onClick={() => setDiscardModalOpen(false)}
                        />
                        <BaseButton
                            type="button"
                            size="md"
                            text={t("editor.discard.confirm")}
                            onClick={() => history.push("/dashboard/product-drafts")}
                        />
                    </div>
                </div>
            </BaseModal>
        </BaseLoadingBlocker>
    );
}
